import { runtimeEvents } from "@shared/runtime-events";
import { blastApi } from "@features/flows/api/blast-api";
import { useRunControlStore } from "../store";
import type { RunStatus, Snapshot } from "../types";
import type { PauseReason } from "../store/run.slice";
import {
  connectSnapshotSse,
  connectTelemetrySse,
  disconnectSnapshotSse,
  disconnectTelemetrySse,
  resetServiceRefs,
  setTelemetryStatusGuard,
} from "./events";
import { isTerminalStatus } from "./reducers";

let opSeq = 0;

interface TerminalWaiter {
  runId: string;
  resolve: (status: RunStatus) => void;
  reject: (error: unknown) => void;
  timer?: ReturnType<typeof setTimeout>;
  aborted?: boolean;
  abortListener?: () => void;
  signal?: AbortSignal;
}

let terminalWaiters: TerminalWaiter[] = [];
let lastSnapshotPaused: boolean | null = null;

const getState = () => useRunControlStore.getState();

const updateTelemetryStatusGuard = () => {
  if (lastSnapshotPaused === true) {
    // Ignore telemetry "running" transitions while the latest snapshot reports a paused state.
    setTelemetryStatusGuard((status) => status !== "running");
  } else {
    // Lift the guard once snapshots show running or the run reaches a terminal state.
    setTelemetryStatusGuard(null);
  }
};

const resolveTerminalWaiters = (status: RunStatus) => {
  const currentRunId = getState().runId;
  if (!currentRunId) return;
  terminalWaiters = terminalWaiters.filter((waiter) => {
    if (waiter.runId !== currentRunId || waiter.aborted) return true;
    if (waiter.timer) clearTimeout(waiter.timer);
    if (waiter.signal && waiter.abortListener) {
      waiter.signal.removeEventListener("abort", waiter.abortListener);
    }
    try {
      waiter.resolve(status);
    } catch {
      // swallow resolution errors
    }
    return false;
  });
};

const rejectAllTerminalWaiters = (reason: unknown) => {
  terminalWaiters.forEach((waiter) => {
    if (waiter.timer) clearTimeout(waiter.timer);
    if (waiter.signal && waiter.abortListener) {
      waiter.signal.removeEventListener("abort", waiter.abortListener);
    }
    if (!waiter.aborted) {
      try {
        waiter.reject(reason);
      } catch {
        // ignore rejection failures
      }
    }
  });
  terminalWaiters = [];
};

const endRun = (final: RunStatus) => {
  if (!isTerminalStatus(final)) return;
  const state = getState();
  state.setStatus(final);
  state.setBusy(false);
  state.setPause(null, null);
  disconnectTelemetrySse();
  disconnectSnapshotSse();
  lastSnapshotPaused = null;
  setTelemetryStatusGuard(null);
  resolveTerminalWaiters(final);
};

const inferPauseReason = (snapshot: Snapshot | null, breakpoints: Set<string>): PauseReason => {
  if (!snapshot) return "unknown";
  const snap: any = snapshot;
  const queues: any = snap.queues ?? {};
  const queueArr: any[] = Array.isArray(queues?.queue) ? queues.queue : [];
  const stackArr: any[] = Array.isArray(queues?.stack) ? queues.stack : [];
  const suspendedArr: any[] = Array.isArray(snap.suspended) ? snap.suspended : [];

  const rawReason =
    snap.reason ??
    snap.pauseReason ??
    snap.meta?.reason ??
    snap.mode ??
    "";
  const lower = typeof rawReason === "string" ? rawReason.toLowerCase() : "";

  const suspendedIds = suspendedArr
    .map((entry: any) => String(entry?.nodeId ?? entry?.node?.id ?? ""))
    .filter(Boolean);

  const hasBreakpointHit = suspendedIds.some((id) => breakpoints.has(id));

  if (lower.includes("break")) return "breakpoint";
  if (lower.includes("start")) return "start-gate";
  if (lower.includes("step")) return "step-mode";
  if (hasBreakpointHit) return "breakpoint";
  if (suspendedArr.length === 0 && (queueArr.length > 0 || stackArr.length > 0)) {
    return "start-gate";
  }
  if (snap.meta?.mode === "step" || snap.mode === "step") return "step-mode";
  return "unknown";
};

const setPausedFromSnapshot = (paused: boolean | null, headNodeId: string | null, latestSnapshot: Snapshot | null) => {
  lastSnapshotPaused = paused;
  updateTelemetryStatusGuard();

  const state = getState();
  const currentStatus = state.status;

  if (paused === true && !isTerminalStatus(currentStatus)) {
    state.setStatus("paused");
    const reason = inferPauseReason(latestSnapshot ?? state.snapshot, state.breakpoints);
    state.setPause(reason, headNodeId);
  } else if (paused === false && !isTerminalStatus(currentStatus)) {
    state.setStatus("running");
    state.setPause(null, null);
  }
};

export const controller = {
  async start(flowId: string, inputs?: Record<string, any>, options?: Record<string, any>) {
    const state = getState();
    if (state.isBusy) return;
    if (state.runId && !isTerminalStatus(state.status)) {
      const msg = "A run is already active. Stop it before starting a new one.";
      state.setError(msg);
      throw new Error(msg);
    }

    controller.reset();

    state.setBusy(true);
    state.setError(null);
    const seq = ++opSeq;

    try {
      const response = await blastApi.startRun({
        flowId,
        inputs,
        options,
        persist: { rootDir: "/tmp/blast/run-data" },
      });
      if (seq !== opSeq) return;

      lastSnapshotPaused = null;
      updateTelemetryStatusGuard();

      const nextState = getState();
      nextState.setIds(response.runId, flowId);
      nextState.setMode("regular");
      nextState.setStatus("running");
      nextState.clearBreakpoints();
      nextState.clearFrames();
      nextState.clearMetrics();
      nextState.clearAllErrors();
      nextState.clearAllOutputs();
      nextState.setSnapshot(null);

      connectTelemetrySse(response.runId, endRun);
      connectSnapshotSse(response.runId, setPausedFromSnapshot, endRun);

      try {
        runtimeEvents.emit("run:reset", { runId: response.runId, flowId });
      } catch {
        // ignore
      }
    } catch (error: any) {
      if (seq === opSeq) {
        const msg = error instanceof Error ? error.message : "Failed to start run";
        state.setError(msg);
      }
      throw error;
    } finally {
      if (seq === opSeq) {
        getState().setBusy(false);
      }
    }
  },

  async startInteractive(flowId: string, inputs?: Record<string, any>, options?: Record<string, any>) {
    const state = getState();
    if (state.isBusy) return;
    if (state.runId && !isTerminalStatus(state.status)) {
      const msg = "A run is already active. Stop it before starting a new one.";
      state.setError(msg);
      throw new Error(msg);
    }

    controller.reset();

    state.setBusy(true);
    state.setError(null);
    const seq = ++opSeq;

    try {
      const response = await blastApi.startInteractiveRun({
        flowId,
        inputs,
        options,
        persist: { rootDir: "/tmp/blast/run-data" },
      });
      if (seq !== opSeq) return;

      lastSnapshotPaused = response.status === "paused" ? true : null;
      updateTelemetryStatusGuard();

      const nextState = getState();
      nextState.setIds(response.runId, flowId);
      nextState.setMode("interactive");
      nextState.clearBreakpoints();
      nextState.clearFrames();
      nextState.clearMetrics();
      nextState.clearAllErrors();
      nextState.clearAllOutputs();
      nextState.setSnapshot(null);

      if (response.status === "paused") {
        nextState.setStatus("paused");
        nextState.setPause((response.reason as PauseReason) ?? "unknown", null);
      } else {
        nextState.setStatus("running");
        nextState.setPause(null, null);
      }

      connectTelemetrySse(response.runId, endRun);
      connectSnapshotSse(response.runId, setPausedFromSnapshot, endRun);

      try {
        runtimeEvents.emit("run:reset", { runId: response.runId, flowId });
      } catch {
        // ignore
      }
    } catch (error: any) {
      if (seq === opSeq) {
        const msg = error instanceof Error ? error.message : "Failed to start interactive run";
        state.setError(msg);
      }
      throw error;
    } finally {
      if (seq === opSeq) {
        getState().setBusy(false);
      }
    }
  },

  async pause() {
    const state = getState();
    const runId = state.runId;
    if (!runId || state.status !== "running") return;
    state.setBusy(true);
    state.setError(null);
    try {
      await blastApi.pauseRun(runId);
      state.setStatus("paused");
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : "Failed to pause run";
      state.setError(msg);
      throw error;
    } finally {
      state.setBusy(false);
    }
  },

  async resume() {
    const state = getState();
    const runId = state.runId;
    if (!runId || state.status !== "paused") return;
    state.setBusy(true);
    state.setError(null);
    try {
      await blastApi.resumeRun(runId);
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : "Failed to resume run";
      state.setError(msg);
      throw error;
    } finally {
      state.setBusy(false);
    }
  },

  async stop() {
    const state = getState();
    const runId = state.runId;
    if (!runId) {
      controller.reset();
      return;
    }
    state.setBusy(true);
    state.setError(null);
    try {
      await blastApi.deleteRun(runId);
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : "Failed to stop run";
      state.setError(msg);
      throw error;
    } finally {
      state.setBusy(false);
      controller.reset();
    }
  },

  async refreshStatus() {
    const state = getState();
    const runId = state.runId;
    if (!runId) return;
    try {
      const response = await blastApi.getRunStatus(runId);
      if (response?.status && !isTerminalStatus(response.status)) {
        state.setStatus(response.status);
      }
      state.setMetrics({
        queueLen: response.queue?.len ?? 0,
        stackLen: response.stack?.len ?? 0,
        suspendedLen: response.suspended?.len ?? 0,
      });
    } catch {
      // ignore refresh errors
    }
  },

  async getSnapshot() {
    const runId = getState().runId;
    if (!runId) return null;
    try {
      const response = await blastApi.getRunSnapshot(runId);
      return response.snapshot ?? null;
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : "Failed to fetch snapshot";
      getState().setError(msg);
      return null;
    }
  },

  async step() {
    const state = getState();
    const runId = state.runId;
    if (!runId || state.mode !== "interactive" || state.status !== "paused") return;
    state.setBusy(true);
    state.setError(null);
    try {
      const response = await blastApi.stepRun(runId);
      if (response.status === "paused") {
        state.setStatus("paused");
        state.setPause("step-mode", state.currentSuspendedNodeId);
      } else if (response.status === "running") {
        state.setStatus("running");
        state.setPause(null, null);
      } else if (isTerminalStatus(response.status)) {
        state.setStatus(response.status);
        state.setPause(null, null);
      }
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : "Failed to step run";
      state.setError(msg);
      throw error;
    } finally {
      state.setBusy(false);
    }
  },

  async continueRun() {
    const state = getState();
    const runId = state.runId;
    if (!runId || state.mode !== "interactive" || state.status !== "paused") return;
    state.setBusy(true);
    state.setError(null);
    try {
      await blastApi.resumeRunSkip(runId);
      state.setStatus("running");
      state.setPause(null, null);
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : "Failed to continue run";
      state.setError(msg);
      throw error;
    } finally {
      state.setBusy(false);
    }
  },

  async addBreakpoints(nodeIds: string[]) {
    const state = getState();
    const runId = state.runId;
    if (!runId || state.mode !== "interactive" || nodeIds.length === 0) return;
    state.setBusy(true);
    state.setError(null);
    try {
      await blastApi.addBreakpoints(runId, { nodeIds });
      state.addBreakpoints(nodeIds);
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : "Failed to add breakpoints";
      state.setError(msg);
      throw error;
    } finally {
      state.setBusy(false);
    }
  },

  async removeBreakpoint(nodeId: string) {
    const state = getState();
    const runId = state.runId;
    if (!runId || state.mode !== "interactive") return;
    state.setBusy(true);
    state.setError(null);
    try {
      await blastApi.removeBreakpoint(runId, nodeId);
      state.removeBreakpointLocal(nodeId);
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : "Failed to remove breakpoint";
      state.setError(msg);
      throw error;
    } finally {
      state.setBusy(false);
    }
  },

  async toggleBreakpoint(nodeId: string) {
    const state = getState();
    if (state.breakpoints.has(nodeId)) {
      await controller.removeBreakpoint(nodeId);
    } else {
      await controller.addBreakpoints([nodeId]);
    }
  },

  async deliver(correlationId: string, payload?: Record<string, any>) {
    const state = getState();
    const runId = state.runId;
    const flowId = state.flowId ?? undefined;
    if (!runId) {
      const msg = "No active run to deliver to";
      state.setError(msg);
      throw new Error(msg);
    }
    state.setBusy(true);
    state.setError(null);
    try {
      await blastApi.deliverRun(runId, correlationId, payload, flowId, { rootDir: "/tmp/blast/run-data" });
      state.setStatus("running");
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : "Failed to deliver to run";
      state.setError(msg);
      throw error;
    } finally {
      state.setBusy(false);
    }
  },

  waitForTerminal(opts?: { timeoutMs?: number; signal?: AbortSignal }): Promise<RunStatus> {
    const state = getState();
    const runId = state.runId;
    const status = state.status;
    if (!runId) return Promise.reject(new Error("No active run"));
    if (isTerminalStatus(status)) return Promise.resolve(status!);

    return new Promise<RunStatus>((resolve, reject) => {
      const waiter: TerminalWaiter = { runId, resolve, reject };

      if (opts?.timeoutMs && opts.timeoutMs > 0) {
        waiter.timer = setTimeout(() => {
          waiter.aborted = true;
          reject(new Error("Timeout waiting for terminal"));
          terminalWaiters = terminalWaiters.filter((w) => w !== waiter);
        }, opts.timeoutMs);
      }

      if (opts?.signal) {
        waiter.signal = opts.signal;
        const onAbort = () => {
          waiter.aborted = true;
          if (waiter.timer) clearTimeout(waiter.timer);
          reject(new DOMException("Aborted", "AbortError"));
          terminalWaiters = terminalWaiters.filter((w) => w !== waiter);
        };
        if (opts.signal.aborted) {
          onAbort();
          return;
        }
        waiter.abortListener = onAbort;
        opts.signal.addEventListener("abort", onAbort, { once: true });
      }

      terminalWaiters.push(waiter);
    });
  },

  reset() {
    opSeq += 1; // invalidate any in-flight operations before clearing state
    rejectAllTerminalWaiters(new DOMException("run cleared", "AbortError"));

    disconnectTelemetrySse();
    disconnectSnapshotSse();
    resetServiceRefs();

    lastSnapshotPaused = null;
    updateTelemetryStatusGuard();

    const state = getState();
    state.clearAll();
    state.clearMetrics();
    state.clearFrames();
    state.clearBreakpoints();
    state.clearAllErrors();
    state.clearAllOutputs();
    state.setBusy(false);
    state.setError(null);

    try {
      runtimeEvents.emit("run:reset", {});
    } catch {
      // ignore
    }
  },
};
